import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Check, Zap, Loader2, X, AlertTriangle,
} from 'lucide-react'
import { InlineWebhookPanel } from './components/InlineWebhookPanel'
import {
  inputCls, labelCls, SectionLabel, CopyButton, RawJsonCollapsible, ResultBadge, ComboSelect,
  EndpointPicker, PayloadEditor, CurlCollapsible,
} from './components/shared'
import type { EndpointDef } from './components/shared'
import { proxyUrl, buildHeaders } from './utils'
import type { Creds, LogEntry, WebhookEvent } from './types'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = Record<string, any>

// ── Constants ──────────────────────────────────────────────────────────────────

const V1_HOSTED_BASE = 'https://api.kycaml.uat.frankiefinancial.io'
const V1_HOSTED_PATH = '/idv/v2/idvalidate/onboarding-url'
const V1_ENTITY_BASE = 'https://api.kycaml.uat.frankiefinancial.io/compliance/v1.2'

const HOSTED_V1_ENDPOINTS: EndpointDef[] = [
  { id: 'generate-url', method: 'POST', label: 'Generate URL', path: '/idv/v2/idvalidate/onboarding-url', category: 'Hosted', core: true },
  { id: 'get-checks', method: 'GET', label: 'Entity Checks', path: '/entity/{entityId}/checks', category: 'Results', core: true },
  // Entity
  { id: 'get-entity', method: 'GET', label: 'Entity Details', path: '/entity/{entityId}', category: 'Entity' },
  // Results
  { id: 'retrieve-response', method: 'GET', label: 'Retrieve Response', path: '/retrieve/response/{requestId}', description: '3-day cache', category: 'Results' },
  { id: 'delete-entity', method: 'DELETE', label: 'Delete Entity', path: '/entity/{entityId}', category: 'Entity Management' },
]

const HOSTED_V1_FLOWS = [
  { id: 'idv', label: 'Biometrics/OCR' },
  { id: 'idv_review', label: 'Biometrics/OCR + Review' },
  { id: 'manual_kyc', label: 'eKYC (form only)' },
  { id: 'doc_upload', label: 'Document Upload (business)' },
  { id: 'individual_doc_upload', label: 'Document Upload (individual)' },
  { id: 'ocr_only', label: 'OCR Only' },
]

// ── Flow step types ───────────────────────────────────────────────────────────

type FlowStepStatus = 'pending' | 'active' | 'done' | 'error'

interface FlowStep {
  id: string
  label: string
  sublabel?: string
  status: FlowStepStatus
}

function FlowConnector() {
  return (
    <div className="flex justify-center">
      <div className="w-0.5 h-5" style={{ background: '#283a57' }} />
    </div>
  )
}

function FlowStepNode({ step }: { step: FlowStep }) {
  const colors: Record<FlowStepStatus, { bg: string; border: string; text: string; icon: string }> = {
    pending: { bg: '#0c1424', border: '#1c2940', text: '#3f4f6b', icon: '#3f4f6b' },
    active: { bg: 'rgba(251,191,36,.12)', border: 'rgba(251,191,36,.5)', text: '#fcd34d', icon: '#fbbf24' },
    done: { bg: 'rgba(52,211,153,.08)', border: 'rgba(52,211,153,.35)', text: '#6ee7b7', icon: '#34d399' },
    error: { bg: 'rgba(248,113,113,.08)', border: 'rgba(248,113,113,.35)', text: '#fca5a5', icon: '#f87171' },
  }
  const c = colors[step.status]
  return (
    <div
      className="rounded-[11px] px-4 py-3"
      style={{ background: c.bg, border: `1px solid ${c.border}` }}
    >
      <div className="flex items-center gap-2">
        {step.status === 'done' && <Check size={12} style={{ color: c.icon }} />}
        {step.status === 'error' && <X size={12} style={{ color: c.icon }} />}
        {step.status === 'active' && <Loader2 size={12} className="animate-spin" style={{ color: c.icon }} />}
        {step.status === 'pending' && <span className="w-3 h-3 rounded-full" style={{ border: `2px solid ${c.icon}` }} />}
        <span className="text-[11px] font-semibold" style={{ color: c.text }}>{step.label}</span>
      </div>
      {step.sublabel && (
        <p className="text-[9px] font-mono mt-1 ml-5" style={{ color: step.status === 'pending' ? '#3f4f6b' : '#5b6b85' }}>{step.sublabel}</p>
      )}
    </div>
  )
}

// ── Checks result display ─────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function kvpMap(notes: any[]): Record<string, string> {
  if (!Array.isArray(notes)) return {}
  const m: Record<string, string> = {}
  for (const n of notes) m[n.kvpKey] = n.kvpValue
  return m
}

function V1ChecksResultPanel({ data, fullResponse }: { data: AnyObj; fullResponse: unknown }) {
  const epr = data?.entityProfileResult
  const cr = data?.checkRisk
  const cs = data?.checkSummary

  const crNotes = kvpMap(cr?.resultNotes ?? [])
  const csNotes = kvpMap(cs?.resultNotes ?? [])
  const riskLevel = crNotes['Risk.Level']
  const confidenceLevel = csNotes['Confidence.Level']
  const failReason = csNotes['Fail.Reason.Partial'] ?? csNotes['Fail.Reason']
  const riskScore = cr?.riskLevel as number | undefined

  const BADGE_RISK_COLOR: Record<string, string> = {
    UNACCEPTABLE: '#f87171', HIGH: '#f87171', MEDIUM: '#fb923c', LOW: '#34d399',
  }

  const riskFactors: { name: string; value: string; score: string }[] = []
  for (const key of Object.keys(crNotes)) {
    const m = key.match(/^risk_factor\.(.+)\.value$/)
    if (m) {
      const factor = m[1]
      riskFactors.push({ name: factor, value: crNotes[key], score: crNotes[`risk_factor.${factor}.score`] ?? '' })
    }
  }

  const matchCounts = [
    { label: 'Name', key: 'Info.NameMatchCount' },
    { label: 'Address', key: 'Info.AddressMatchCount' },
    { label: 'DoB', key: 'Info.DoBMatchCount' },
    { label: 'Gov ID', key: 'Info.GovIDMatchCount' },
    { label: 'Other ID', key: 'Info.OtherIDMatchCount' },
  ].filter(m => csNotes[m.key] != null)

  const outcomeColor = epr?.actionRecommended === 'PASS' ? '#34d399'
    : epr?.actionRecommended === 'FAIL' ? '#f87171'
    : epr?.actionRecommended === 'REFER' ? '#fb923c'
    : '#94a3b8'

  return (
    <div className="space-y-3">
      {/* Verdict header */}
      {epr && (
        <div
          className="p-4 rounded-[14px]"
          style={{
            background: `${outcomeColor}08`,
            border: `1px solid ${outcomeColor}30`,
          }}
        >
          <div className="flex items-start gap-4">
            <div
              className="w-[46px] h-[46px] rounded-[12px] flex items-center justify-center shrink-0"
              style={{ background: `${outcomeColor}20`, border: `1px solid ${outcomeColor}30` }}
            >
              {epr.actionRecommended === 'PASS' && <Check size={22} style={{ color: outcomeColor }} />}
              {epr.actionRecommended === 'FAIL' && <X size={22} style={{ color: outcomeColor }} />}
              {epr.actionRecommended === 'REFER' && <AlertTriangle size={22} style={{ color: outcomeColor }} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[19px] font-extrabold" style={{ color: outcomeColor }}>{epr.actionRecommended}</span>
                {riskLevel && (
                  <span
                    className="text-[10px] font-bold px-2 py-0.5 rounded-[6px]"
                    style={{ background: `${BADGE_RISK_COLOR[riskLevel] ?? '#94a3b8'}20`, color: BADGE_RISK_COLOR[riskLevel] ?? '#94a3b8' }}
                  >
                    RISK {riskLevel}{riskScore != null ? ` · ${riskScore}` : ''}
                  </span>
                )}
                {confidenceLevel && (
                  <span className="text-[10px] font-mono text-[#5b6b85]">Confidence: {confidenceLevel}</span>
                )}
              </div>
              {failReason && (
                <p className="text-[11px] text-[#fca5a5] mt-1">{failReason}</p>
              )}
              {epr.entityId && (
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] text-[#5b6b85]">entityId</span>
                  <span className="text-[10px] font-mono text-[#94a3b8] truncate">{epr.entityId}</span>
                  <CopyButton text={epr.entityId} />
                </div>
              )}
              {epr.latestCheckDate && (
                <p className="text-[9px] font-mono text-[#3f4f6b] mt-1">
                  latestCheckDate: {epr.latestCheckDate}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Check results table */}
      {epr?.checkResults?.length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Check Results</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#1a2740' }}>
            {epr.checkResults.map((check: AnyObj, i: number) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[11px] text-[#94a3b8] flex-1 font-mono">{check.name ?? check.checkType ?? '—'}</span>
                <ResultBadge result={check.result ?? '—'} />
                {check.message && <span className="text-[9px] text-[#5b6b85] italic truncate max-w-[200px]">{check.message}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Match counts */}
      {matchCounts.length > 0 && (
        <div className="rounded-[11px] p-4" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-3">Match Counts</p>
          <div className="flex items-center gap-4 flex-wrap">
            {matchCounts.map(m => {
              const count = parseInt(csNotes[m.key] ?? '0')
              return (
                <div key={m.label} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-[#5b6b85]">{m.label}:</span>
                  <span className="font-bold font-mono" style={{ color: count > 0 ? '#34d399' : '#475569' }}>{count}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Risk factors */}
      {riskFactors.length > 0 && (
        <div className="rounded-[11px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="px-4 py-2.5" style={{ borderBottom: '1px solid #1a2740', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Risk Factors</p>
          </div>
          <div className="divide-y" style={{ borderColor: '#1a2740' }}>
            {riskFactors.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2">
                <span className="text-[11px] text-[#94a3b8] flex-1 font-mono">{f.name.replace(/_/g, ' ')}</span>
                <span className="text-[10px] font-mono text-[#5b6b85]">{f.value}</span>
                <span className="text-[10px] font-mono text-[#94a3b8] w-8 text-right">{f.score || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Issue list */}
      {epr?.issueList?.length > 0 && (
        <div className="rounded-[11px] p-3 space-y-1.5" style={{ border: '1px solid rgba(248,113,113,.3)', background: 'rgba(248,113,113,.06)' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={13} className="text-red-400" />
            <p className="text-[11px] font-semibold text-red-400">Issues</p>
          </div>
          {epr.issueList.map((issue: string, i: number) => (
            <div key={i} className="text-[11px] text-[#fca5a5] font-mono">{issue}</div>
          ))}
        </div>
      )}

      <RawJsonCollapsible data={fullResponse} />
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface HostedV1WorkspaceProps {
  creds: Creds
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
}

export function HostedV1Workspace({ creds, addLog }: HostedV1WorkspaceProps) {
  const hasCreds = !!(creds.customerId && creds.apiKey)
  const v1Creds = { ...creds, apiBase: V1_HOSTED_BASE }

  const [selectedEndpoint, setSelectedEndpoint] = useState<string>('generate-url')
  const [flowId, setFlowId] = useState('idv')
  const [customerRef, setCustomerRef] = useState(`test-${Date.now()}`)
  const [existingEntityId, setExistingEntityId] = useState('')
  const [manualEntityId, setManualEntityId] = useState('')
  const [getEntityIdInput, setGetEntityIdInput] = useState('')
  const [sendSms, setSendSms] = useState(false)
  const [phoneCode, setPhoneCode] = useState('+61')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [loading, setLoading] = useState(false)
  const [checksLoading, setChecksLoading] = useState(false)
  const [hostedUrl, setHostedUrl] = useState<string | null>(null)
  const [entityId, setEntityId] = useState<string | null>(null)
  const [events, setEvents] = useState<{ ts: string; [k: string]: unknown }[]>([])
  const [checksResult, setChecksResult] = useState<AnyObj | null>(null)
  const [fullChecksResult, setFullChecksResult] = useState<unknown>(null)
  const [webhookEvents, setWebhookEvents] = useState<WebhookEvent[]>([])
  const autoFetchedRef = useRef(false)

  // PayloadEditor state
  const [editorText, setEditorText] = useState('')
  const [editorDirty, setEditorDirty] = useState(false)
  const [editorError, setEditorError] = useState<string | null>(null)

  const epDef = HOSTED_V1_ENDPOINTS.find(e => e.id === selectedEndpoint) ?? HOSTED_V1_ENDPOINTS[0]
  const isGetEndpoint = epDef.method === 'GET'

  function derivedPayload(): Record<string, unknown> {
    const body: Record<string, unknown> = { consent: true, flowId, customerRef }
    if (existingEntityId.trim()) body.entity = { entityId: existingEntityId.trim() }
    if (sendSms && phoneNumber.trim()) {
      body.sendSMS = true; body.phoneNumber = phoneNumber.trim(); body.phoneCode = phoneCode
      if (recipientName.trim()) body.recipientName = recipientName.trim()
    }
    return body
  }

  function dynamicPath(): string {
    if (selectedEndpoint === 'generate-url') return V1_HOSTED_PATH
    const eid = entityId ?? getEntityIdInput
    return `/entity/${eid || '{entityId}'}/checks`
  }

  const generateUrlFull = `${V1_HOSTED_BASE}${dynamicPath()}`
  const resolvedChecksEid = entityId ?? getEntityIdInput
  const getChecksFull = `${V1_ENTITY_BASE}/entity/${resolvedChecksEid || '{entityId}'}/checks`
  const fullUrl = selectedEndpoint === 'generate-url' ? generateUrlFull : getChecksFull

  // Sync derived payload to editor
  const derivedJson = JSON.stringify(derivedPayload())
  useEffect(() => {
    if (!editorDirty && selectedEndpoint === 'generate-url') {
      try {
        setEditorText(JSON.stringify(JSON.parse(derivedJson), null, 2))
      } catch {
        setEditorText(derivedJson)
      }
      setEditorError(null)
    }
  }, [derivedJson, editorDirty, selectedEndpoint])

  // Auto-populate GET input from prior results
  useEffect(() => {
    if (entityId && !getEntityIdInput) setGetEntityIdInput(entityId)
  }, [entityId, getEntityIdInput])

  function handleEndpointSelect(id: string) {
    setSelectedEndpoint(id)
    setEditorDirty(false)
    setEditorError(null)
  }

  function handleEditorChange(v: string) { setEditorText(v); setEditorDirty(true); setEditorError(null) }
  function resetEditor() { setEditorDirty(false); setEditorText(JSON.stringify(derivedPayload(), null, 2)); setEditorError(null) }
  function getEditorPayload(): Record<string, unknown> | null {
    try { return JSON.parse(editorText) as Record<string, unknown> }
    catch { setEditorError('Invalid JSON — fix syntax before sending'); return null }
  }

  // postMessage listener for hosted iframe events
  useEffect(() => {
    function handler(e: MessageEvent) {
      if (e.data?.['X-Frankie-Source'] === 'hosted-onesdk') {
        const msg = { ts: new Date().toLocaleTimeString(), ...e.data as Record<string, unknown> }
        setEvents(prev => [...prev, msg])
        if (e.data?.entityId) setEntityId(e.data.entityId as string)
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [])

  // GET /entity/{entityId}/checks
  const getChecks = useCallback(async (eid?: string) => {
    const resolvedId = eid ?? entityId ?? getEntityIdInput.trim() ?? manualEntityId.trim()
    if (!resolvedId) return
    setChecksLoading(true)
    const url = `${V1_ENTITY_BASE}/entity/${resolvedId}/checks`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/entity/${resolvedId}/checks`), {
        method: 'GET',
        headers: buildHeaders({ ...v1Creds, apiBase: V1_ENTITY_BASE }),
      })
      const durationMs = Date.now() - start
      const data = await res.json() as AnyObj
      addLog({ method: 'GET', url, status: res.status, durationMs, responseBody: data, annotation: 'V1 Hosted: GET /entity/{entityId}/checks — full verification result' })
      if (res.ok) { setFullChecksResult(data); setChecksResult(data) }
    } catch { /* logged */ } finally { setChecksLoading(false) }
  }, [entityId, getEntityIdInput, manualEntityId, v1Creds, addLog])

  // Auto-fetch checks when UpdateCheckEntity webhook arrives
  const prevWebhookCountRef = useRef(0)
  useEffect(() => {
    if (webhookEvents.length <= prevWebhookCountRef.current) return
    prevWebhookCountRef.current = webhookEvents.length
    const hasUpdateCheck = webhookEvents.some(e =>
      e.payload?.function === 'UpdateCheckEntity' && e.payload?.functionResult === 'COMPLETED'
    )
    if (hasUpdateCheck && entityId && !checksResult && !autoFetchedRef.current) {
      autoFetchedRef.current = true
      void getChecks(entityId)
    }
  }, [webhookEvents, entityId, checksResult, getChecks])

  async function generateUrl() {
    const payload = getEditorPayload() ?? derivedPayload()
    setLoading(true); setHostedUrl(null); setEntityId(null); setChecksResult(null); setFullChecksResult(null)
    setEvents([]); autoFetchedRef.current = false
    const url = `${V1_HOSTED_BASE}${V1_HOSTED_PATH}`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(V1_HOSTED_PATH), {
        method: 'POST',
        headers: buildHeaders(v1Creds, true),
        body: JSON.stringify(payload),
      })
      const durationMs = Date.now() - start
      const data = await res.json() as AnyObj
      addLog({ method: 'POST', url, status: res.status, durationMs, requestBody: payload, responseBody: data, annotation: 'V1 Hosted: Generate onboarding URL' })
      const generatedUrl = data?.url ?? data?.onboardingUrl ?? data?.data?.url
      const eid = data?.entityId ?? data?.entity?.entityId
      if (res.ok && generatedUrl) {
        setHostedUrl(String(generatedUrl))
        if (eid) { setEntityId(String(eid)); setGetEntityIdInput(String(eid)) }
      }
    } catch { /* logged */ } finally { setLoading(false) }
  }

  function reset() {
    setHostedUrl(null); setEntityId(null); setEvents([]); setChecksResult(null)
    setFullChecksResult(null); setManualEntityId(''); setWebhookEvents([])
    setGetEntityIdInput(''); autoFetchedRef.current = false
  }

  const activeEntityId = (entityId ?? getEntityIdInput.trim() ?? manualEntityId.trim()) || null

  // Derive postMessage status
  const hasSuccess = events.some(e => String(e.event ?? '').endsWith(':success'))
  const hasFailed = events.some(e => String(e.event ?? '').endsWith(':failed') || String(e.event ?? '').endsWith(':error'))

  // Derive webhook status
  const hasWebhooks = webhookEvents.length > 0
  const webhookStatusChange = webhookEvents.find(e => e.payload?.function === 'EntityStatusChange')
  const webhookUpdateCheck = webhookEvents.find(e => e.payload?.function === 'UpdateCheckEntity')

  // Build flow steps
  const flowSteps: FlowStep[] = [
    {
      id: 'generate',
      label: '1. Generate Hosted URL',
      sublabel: hostedUrl ? `POST ${V1_HOSTED_PATH}` : undefined,
      status: hostedUrl ? 'done' : loading ? 'active' : 'pending',
    },
    {
      id: 'iframe',
      label: '2. User completes Hosted flow',
      sublabel: hostedUrl
        ? events.length > 0
          ? `${events.length} postMessage event${events.length !== 1 ? 's' : ''} received`
          : 'Waiting for user to complete flow in iframe…'
        : undefined,
      status: hasSuccess || hasFailed
        ? hasFailed ? 'error' : 'done'
        : hostedUrl ? 'active' : 'pending',
    },
    {
      id: 'postmessage',
      label: '3. postMessage: idv:success',
      sublabel: hasSuccess
        ? 'User finished UI — workflow may still be running server-side'
        : hasFailed
        ? 'Flow errored or failed'
        : undefined,
      status: hasSuccess ? 'done' : hasFailed ? 'error' : 'pending',
    },
    {
      id: 'webhooks',
      label: '4. Wait for UpdateCheckEntity webhook',
      sublabel: hasWebhooks
        ? webhookUpdateCheck
          ? `UpdateCheckEntity: COMPLETED — checks finished, safe to fetch result`
          : webhookStatusChange
          ? `EntityStatusChange: ${String(webhookStatusChange.payload?.message ?? '').slice(0, 60)}`
          : `${webhookEvents.length} webhook${webhookEvents.length !== 1 ? 's' : ''} (waiting for UpdateCheckEntity…)`
        : hasSuccess ? 'Waiting for webhook — do NOT call API yet (race condition)' : undefined,
      status: webhookUpdateCheck ? 'done'
        : hasWebhooks && !webhookUpdateCheck ? 'active'
        : (hasSuccess || hasFailed) ? 'active' : 'pending',
    },
    {
      id: 'checks',
      label: '5. GET /entity/{entityId}/checks',
      sublabel: checksResult
        ? `actionRecommended: ${checksResult.entityProfileResult?.actionRecommended ?? '—'}`
        : checksLoading
        ? 'Fetching verification result…'
        : webhookUpdateCheck
        ? 'Webhook received — now safe to call'
        : (hasSuccess || hasFailed)
        ? 'Wait for UpdateCheckEntity webhook before calling'
        : undefined,
      status: checksResult
        ? checksResult.entityProfileResult?.actionRecommended === 'PASS' ? 'done' : 'error'
        : checksLoading ? 'active' : 'pending',
    },
  ]

  const curlBody = isGetEndpoint ? undefined : (editorDirty ? (getEditorPayload() ?? undefined) : derivedPayload())

  return (
    <div
      className="grid gap-4"
      style={{ gridTemplateColumns: '200px minmax(350px, 400px) 1fr', alignItems: 'start', padding: '18px 0' }}
    >
      {/* ── COLUMN 1: Endpoint Picker ── */}
      <EndpointPicker
        endpoints={HOSTED_V1_ENDPOINTS}
        selectedId={selectedEndpoint}
        onSelect={handleEndpointSelect}
      />

      {/* ── COLUMN 2: Form Panel ── */}
      <div className="rounded-[14px] overflow-hidden" style={{ background: '#111c30', border: '1px solid #1e2c44' }}>
        {/* Header strip */}
        <div className="flex items-center gap-2.5 px-4 py-3" style={{ background: '#0e1828', borderBottom: '1px solid #1e2c44' }}>
          <span
            className="px-2 py-0.5 rounded text-[10px] font-bold font-mono"
            style={isGetEndpoint
              ? { background: 'rgba(96,165,250,.15)', color: '#60a5fa' }
              : { background: 'rgba(52,211,153,.13)', color: '#34d399' }}
          >
            {epDef.method}
          </span>
          <span className="text-[11px] font-mono text-[#8aa0c2] truncate flex-1">{epDef.path}</span>
          <span className="text-[9px] font-mono text-[#3f4f6b]">V1</span>
        </div>

        <div className="p-4 space-y-4">
          {/* generate-url form */}
          {selectedEndpoint === 'generate-url' && (
            <>
              <div>
                <label className={labelCls}>Flow ID <span style={{ color: '#f87171' }}>*</span></label>
                <ComboSelect
                  value={flowId}
                  onChange={setFlowId}
                  options={HOSTED_V1_FLOWS.map(f => ({ value: f.id, label: f.label }))}
                  placeholder="e.g. idv, manual_kyc, or custom flow"
                  className={inputCls}
                />
              </div>

              <div>
                <label className={labelCls}>Customer Ref <span style={{ color: '#f87171' }}>*</span></label>
                <input value={customerRef} onChange={e => setCustomerRef(e.target.value)} className={inputCls} placeholder="unique-reference-for-this-user" />
              </div>

              <div>
                <label className={labelCls}>Entity ID <span className="normal-case font-normal text-[#5b6b85]">(optional — existing entity)</span></label>
                <input value={existingEntityId} onChange={e => setExistingEntityId(e.target.value)} className={inputCls} placeholder="omit to create new" />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={sendSms} onChange={e => setSendSms(e.target.checked)} style={{ accentColor: '#6366f1' }} />
                  <span className="text-[11px] text-[#94a3b8]">Send link via SMS</span>
                </label>
                {sendSms && (
                  <div className="space-y-2 pl-1">
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className={labelCls}>Code</label>
                        <input value={phoneCode} onChange={e => setPhoneCode(e.target.value)} className={inputCls} placeholder="+61" />
                      </div>
                      <div className="col-span-3">
                        <label className={labelCls}>Phone Number</label>
                        <input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} className={inputCls} placeholder="0412345678" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Recipient Name <span className="normal-case font-normal text-[#5b6b85]">(optional)</span></label>
                      <input value={recipientName} onChange={e => setRecipientName(e.target.value)} className={inputCls} placeholder="Jane Smith" />
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-1">
                <button
                  onClick={() => void generateUrl()}
                  disabled={loading || !hasCreds || !flowId || !customerRef || (sendSms && !phoneNumber.trim())}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {loading
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Generating…</span>
                    : 'Generate Hosted URL (V1)'
                  }
                </button>

                {(hostedUrl || entityId) && (
                  <button onClick={reset} className="w-full py-2 rounded-[8px] text-[12px] font-medium transition-colors"
                    style={{ background: '#16223a', border: '1px solid #283a57', color: '#c7d2fe' }}>
                    <X size={11} className="inline mr-1" />Reset
                  </button>
                )}

                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}

                {entityId && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-[8px]" style={{ background: 'rgba(52,211,153,.06)', border: '1px solid rgba(52,211,153,.2)' }}>
                    <span className="text-[10px] font-semibold text-emerald-400">entityId</span>
                    <span className="text-[10px] font-mono text-[#94a3b8] flex-1 truncate">{entityId}</span>
                    <CopyButton text={entityId} />
                  </div>
                )}

                {hostedUrl && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-[#5b6b85]">Hosted URL:</span>
                      <CopyButton text={hostedUrl} />
                    </div>
                    <a href={hostedUrl} target="_blank" rel="noreferrer" className="text-[10px] font-mono break-all hover:underline block" style={{ color: '#a78bfa' }}>{hostedUrl}</a>
                  </div>
                )}
              </div>
            </>
          )}

          {/* get-checks form */}
          {selectedEndpoint === 'get-checks' && (
            <>
              <div>
                <label className={labelCls}>Entity ID <span style={{ color: '#f87171' }}>*</span></label>
                <input
                  className={inputCls}
                  value={getEntityIdInput}
                  onChange={e => setGetEntityIdInput(e.target.value)}
                  placeholder="entityId from generate URL step or postMessage"
                />
              </div>

              <div className="space-y-2 pt-1">
                {!hasCreds && <p className="text-[10px] text-amber-400 text-center">Configure credentials above to make live calls</p>}
                <button
                  onClick={() => void getChecks()}
                  disabled={checksLoading || !hasCreds || !activeEntityId}
                  className="w-full py-2.5 rounded-[9px] text-[13px] font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: '#6366f1', boxShadow: '0 3px 12px rgba(99,102,241,.45)' }}
                >
                  {checksLoading
                    ? <span className="flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" />Fetching…</span>
                    : `GET /entity/${activeEntityId || '{entityId}'}/checks`
                  }
                </button>
                <p className="text-[9px] text-[#3f4f6b] font-mono">
                  Returns entityProfileResult.actionRecommended (PASS/FAIL/REFER)
                </p>
              </div>

              {/* Manual entity ID fallback */}
              {!entityId && (
                <div>
                  <SectionLabel>Manual Entity ID</SectionLabel>
                  <input value={manualEntityId} onChange={e => setManualEntityId(e.target.value)} className={inputCls} placeholder="Enter entity ID from portal or postMessage" />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── COLUMN 3: Results — always-visible panels ── */}
      <div className="space-y-3 min-w-0">
        {/* PayloadEditor + cURL — above webhooks */}
        {!isGetEndpoint && (
          <PayloadEditor
            value={editorText}
            dirty={editorDirty}
            error={editorError}
            onChange={handleEditorChange}
            onReset={resetEditor}
          />
        )}
        {selectedEndpoint === 'generate-url' ? (
          <CurlCollapsible method={epDef.method} url={fullUrl} creds={v1Creds} body={curlBody} />
        ) : (
          <CurlCollapsible method={epDef.method} url={getChecksFull} creds={{ ...v1Creds, apiBase: V1_ENTITY_BASE }} />
        )}

        {/* Webhooks */}
        <InlineWebhookPanel
          entityId={activeEntityId}
          customerId={creds.customerId || null}
          onEventsChange={evts => setWebhookEvents(evts)}
        />

        {/* Flow visualization */}
        <div className="rounded-[14px] p-4" style={{ background: '#0e1828', border: '1px solid #1e2c44' }}>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85] mb-3">Hosted V1 — Verification Flow</p>
          {flowSteps.map((step, i) => (
            <div key={step.id}>
              <FlowStepNode step={step} />
              {i < flowSteps.length - 1 && <FlowConnector />}
            </div>
          ))}
        </div>

        {/* iframe */}
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">Hosted Verification iframe (V1)</p>
            <div className="flex items-center gap-3">
              {hostedUrl && <p className="text-[10px] text-[#5b6b85] font-mono">{new URL(hostedUrl).host}</p>}
              {hostedUrl && (
                <a href={hostedUrl} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors"
                  style={{ background: 'rgba(167,139,250,.15)', border: '1px solid rgba(167,139,250,.3)', color: '#a78bfa' }}>
                  ↗ Open in new tab
                </a>
              )}
            </div>
          </div>
          {hostedUrl ? (
            <iframe
              src={hostedUrl}
              allow="camera;fullscreen;accelerometer;gyroscope;magnetometer;geolocation;microphone;"
              className="w-full"
              style={{ minHeight: 620, border: 0 }}
              title="FrankieOne Hosted Verification V1"
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <Zap size={22} className="text-[#3f4f6b] mb-3" />
              <p className="text-[11px] text-[#475569] italic">Generate a hosted URL to load the iframe here</p>
            </div>
          )}
        </div>

        {/* postMessage events */}
        <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #1e2c44', background: '#0e1828' }}>
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid #1e2c44', background: '#0a1322' }}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5b6b85]">postMessage Events</span>
              {events.length > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(167,139,250,.2)', color: '#a78bfa' }}>{events.length}</span>}
            </div>
            {events.length > 0 && <button onClick={() => setEvents([])} className="text-[10px] text-[#5b6b85] hover:text-red-400 transition-colors">Clear</button>}
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto' }}>
            {events.length === 0 ? (
              <p className="text-[10px] text-[#475569] p-3 italic">{hostedUrl ? 'Iframe loaded — complete flow to see events' : 'No events yet'}</p>
            ) : events.map((ev, i) => {
              const evName = String(ev.event ?? ev.type ?? '')
              const isSuccess = evName.endsWith(':success') || evName.endsWith(':complete')
              const isError = evName.endsWith(':failed') || evName.endsWith(':error')
              return (
                <div key={i} className="px-4 py-1.5 flex items-center gap-2 border-b" style={{ borderColor: '#1a2740' }}>
                  <span className="font-mono text-[9px] text-[#5b6b85] w-20 shrink-0">{String(ev.ts)}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-mono font-medium" style={{
                    background: isError ? 'rgba(248,113,113,.15)' : isSuccess ? 'rgba(52,211,153,.15)' : 'rgba(148,163,184,.15)',
                    color: isError ? '#f87171' : isSuccess ? '#34d399' : '#94a3b8',
                  }}>{evName}</span>
                  {isSuccess && (
                    <span className="text-[9px] text-amber-400 ml-auto">≠ PASS (flow completed, not verification result)</span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Checks result */}
        {checksResult && (
          <div style={{ animation: 'fadeUp .25s ease' }}>
            <V1ChecksResultPanel data={checksResult} fullResponse={fullChecksResult} />
          </div>
        )}

        {/* Idle state */}
        {!hostedUrl && !checksResult && (
          <div
            className="flex flex-col items-center justify-center py-12 rounded-[14px]"
            style={{ border: '2px dashed #233149', background: '#0d1626' }}
          >
            <p className="text-[14px] font-semibold text-[#5b6b85]">Hosted V1 Workspace</p>
            <p className="text-[11px] text-[#3f4f6b] mt-1.5 text-center max-w-xs">
              Generate a hosted URL, complete the verification flow, then call GET /entity/&#123;entityId&#125;/checks to see the definitive result.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
